import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useApps } from "@/api/useApps";
import { useCharts } from "@/api/useCharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ComparisonFrame } from "@/components/ComparisonFrame";
import {
  COMPARISON_EXIT_MS,
  ComparisonReveal,
} from "@/components/ComparisonReveal";
import {
  BrowserCanvas,
  type ExplorerTarget,
} from "@/components/BrowserCanvas";
import { ComparisonSet } from "@/components/ComparisonSet";
import {
  DIFF_MODE_VALUES,
  type DiffMode,
  type ViewMode,
  canonicalHidden,
  includedItems,
  parseCompareSearch,
} from "@/lib/search-params";
import { useComparisonItems } from "@/lib/comparison-items";

interface CompareSearchInput {
  items?: string;
  baseline?: string;
  view?: ViewMode;
  diffMode?: DiffMode;
  pair?: string;
  hidden?: string;
}

// Comparison route. Search-params drive the comparison set — every
// URL is a complete recipe (AGENTS.md). Chart versions is `/versions`.
export const Route = createFileRoute("/compare")({
  component: ComparePage,
  validateSearch: (raw: Record<string, unknown>): CompareSearchInput => ({
    items: typeof raw.items === "string" ? raw.items : undefined,
    baseline: typeof raw.baseline === "string" ? raw.baseline : undefined,
    view:
      raw.view === "single" || raw.view === "diff"
        ? (raw.view as ViewMode)
        : undefined,
    diffMode: (DIFF_MODE_VALUES as readonly string[]).includes(
      typeof raw.diffMode === "string" ? raw.diffMode : "",
    )
      ? (raw.diffMode as DiffMode)
      : undefined,
    pair: typeof raw.pair === "string" ? raw.pair : undefined,
    hidden: typeof raw.hidden === "string" ? raw.hidden : undefined,
  }),
});

function ComparePage(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/compare" });

  const apps = useApps();
  const charts = useCharts();

  const state = useMemo(() => parseCompareSearch(search), [search]);

  const pairScrollY = useRef<number | null>(null);

  const updateState = useCallback(
    (
      next: {
        items?: readonly string[];
        baseline?: string | undefined;
        view?: ViewMode;
        diffMode?: DiffMode;
        pair?: string | undefined;
        hidden?: readonly string[];
      },
      options?: { resetScroll?: boolean },
    ) => {
      const items = next.items ?? state.items;
      const baseline =
        next.baseline !== undefined
          ? next.baseline
          : items.includes(state.baseline ?? "")
            ? state.baseline
            : items[0];
      const view = next.view ?? state.view;
      const diffMode = next.diffMode ?? state.diffMode;
      const pair = next.pair !== undefined ? next.pair : state.pair;
      const hidden = canonicalHidden(
        next.hidden !== undefined ? next.hidden : state.hidden,
        items,
        baseline,
      );
      navigate({
        search: () => ({
          items: items.length > 0 ? items.join(",") : undefined,
          baseline: baseline,
          view: view !== "diff" ? view : undefined,
          diffMode: diffMode !== "side-by-side" ? diffMode : undefined,
          pair:
            pair && items.includes(pair) && pair !== baseline
              ? pair
              : undefined,
          hidden: hidden.length > 0 ? hidden.join(",") : undefined,
        }),
        replace: true,
        ...(options?.resetScroll === false ? { resetScroll: false } : {}),
      });
    },
    [navigate, state],
  );

  useLayoutEffect(() => {
    const saved = pairScrollY.current;
    if (saved === null) return;
    pairScrollY.current = null;
    if (window.scrollY !== saved) {
      window.scrollTo(0, saved);
    }
  });

  const { addItem, removeItem, setBaseline, moveItem } = useComparisonItems(
    { items: state.items, baseline: state.baseline },
    ({ items, baseline }) => updateState({ items, baseline, pair: undefined }),
  );

  const setPair = useCallback(
    (pair: string) => {
      if (state.pair === pair) return;
      pairScrollY.current = window.scrollY;
      updateState({ pair }, { resetScroll: false });
    },
    [state.pair, updateState],
  );

  const [explorerTarget, setExplorerTarget] = useState<ExplorerTarget | null>(
    null,
  );
  const hasItems = state.items.length > 0;
  const hadItems = useRef(hasItems);
  const [holdDock, setHoldDock] = useState(false);

  useEffect(() => {
    if (hasItems) {
      hadItems.current = true;
      setHoldDock(false);
      return;
    }
    if (!hadItems.current) return;
    hadItems.current = false;
    if (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setHoldDock(false);
      return;
    }
    setHoldDock(true);
    const timer = window.setTimeout(
      () => setHoldDock(false),
      COMPARISON_EXIT_MS,
    );
    return () => window.clearTimeout(timer);
  }, [hasItems]);

  const exploreVersions = useCallback((repoSlug: string, chart: string) => {
    setExplorerTarget({ repoSlug, chart });
  }, []);

  const toggleHidden = useCallback(
    (id: string) => {
      if (!state.items.includes(id) || id === state.baseline) return;
      const hidden = state.hidden.includes(id)
        ? state.hidden.filter((entry) => entry !== id)
        : [...state.hidden, id];
      updateState({ hidden }, { resetScroll: false });
    },
    [state.baseline, state.hidden, state.items, updateState],
  );

  const included = useMemo(
    () => includedItems(state.items, state.hidden),
    [state.hidden, state.items],
  );

  const setDiffMode = useCallback(
    (diffMode: DiffMode) => {
      if (state.diffMode === diffMode) return;
      updateState({ diffMode });
    },
    [state.diffMode, updateState],
  );

  return (
    <div className="flex flex-col gap-6">
      <BrowserCanvas
        apps={apps.data}
        charts={charts.data}
        isAppsLoading={apps.isLoading}
        isChartsLoading={charts.isLoading}
        appsError={apps.error}
        chartsError={charts.error}
        onRetryApps={() => {
          void apps.refetch();
        }}
        onRetryCharts={() => {
          void charts.refetch();
        }}
        comparisonItems={state.items}
        holdDock={holdDock}
        onAdd={addItem}
        explorer={explorerTarget}
        onExplore={exploreVersions}
        onCloseExplorer={() => setExplorerTarget(null)}
      />

      <ComparisonReveal visible={hasItems}>
        <Card>
          <CardHeader>
            <CardTitle>
              Comparison set ({state.items.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ComparisonSet
              items={state.items}
              baseline={state.baseline}
              apps={apps.data}
              onRemove={removeItem}
              onSetBaseline={setBaseline}
              onMove={moveItem}
              hidden={state.hidden}
              onToggleHidden={toggleHidden}
              onAddDefaultsForApp={(chartItemId) => addItem(chartItemId)}
              onExploreVersionsForApp={exploreVersions}
              charts={charts.data}
              chartsLoading={charts.isLoading}
            />
          </CardContent>
        </Card>
      </ComparisonReveal>

      <ComparisonReveal visible={hasItems} delayed>
        <ComparisonFrame
          items={included}
          membership={state.items}
          baseline={state.baseline}
          diffMode={state.diffMode}
          pair={state.pair}
          onDiffModeChange={setDiffMode}
          onPairChange={setPair}
        />
      </ComparisonReveal>
    </div>
  );
}
